/**
 * Ticket slip / betslip helper utilities.
 * Used for outcome picking, bet type resolution, and odds / payout calculation.
 */

import type {
  SystemBetRow,
  TicketBetMode,
  TicketSlipResolvedType,
  TicketStakeMode,
} from 'features/tickets/types';

/** Outcome item as used in events (may have extra fields attached by pickOutcome). */
export interface OutcomeItem {
  outcome?: unknown;
  outcome_id?: number;
  market_type_id?: number;
  event_id?: number | string;
  sport_id?: number | string;
  n_runners?: number;
  runner_number?: number;
  description?: string | null;
  [key: string]: unknown;
}

/** Event shape expected by pickOutcome (outcomes may be array or record). */
export interface PickOutcomeEvent {
  event_id?: number | string;
  sport_id?: number | string;
  outcomes?: OutcomeItem[] | Record<string, OutcomeItem>;
  runners?: Array<{ runner_id?: number | string }>;
  n_runners?: number;
  description?: string | null;
  home_team?: { code?: string };
  away_team?: { code?: string };
  home_team_code?: string;
  away_team_code?: string;
}

export interface PickOutcomeParams {
  event: PickOutcomeEvent | null | undefined;
  pick_outcome: unknown;
  market_type_id?: number | string;
}

/**
 * Picks an outcome from the event by pick_outcome (and optionally market_type_id),
 * attaches extra fields (outcome_id, event_id, sport_id, n_runners, runner_number, description),
 * and returns the outcome object or false if not found.
 */
export function pickOutcome({
  event,
  pick_outcome,
  market_type_id,
}: PickOutcomeParams): OutcomeItem | false {
  const rawOutcomes = event?.outcomes;
  const outcomes: OutcomeItem[] = Array.isArray(rawOutcomes)
    ? rawOutcomes
    : rawOutcomes && typeof rawOutcomes === 'object'
      ? (Object.values(rawOutcomes) as OutcomeItem[])
      : [];

  let outcome_id = -1;
  if (market_type_id) {
    outcome_id = outcomes.findIndex(
      (item) =>
        item &&
        item.outcome != null &&
        pick_outcome != null &&
        String(item.outcome) === String(pick_outcome) &&
        parseInt(String(item.market_type_id)) === parseInt(String(market_type_id))
    );
  } else {
    outcome_id = outcomes.findIndex(
      (item) =>
        item &&
        item.outcome != null &&
        pick_outcome != null &&
        String(item.outcome) == String(pick_outcome)
    );
  }
  if (outcome_id < 0) {
    return false;
  }

  const out = outcomes[outcome_id] as Record<string, unknown>;
  out['outcome_id'] = (outcomes[outcome_id]!.outcome_id ?? outcome_id) as number;
  out['event_id'] = event!.event_id;
  out['sport_id'] = event!.sport_id;

  out['n_runners'] = event!.n_runners;
  const sportIdNum = parseInt(String(event?.sport_id), 10);
  const isRacing = sportIdNum === 10 || sportIdNum === 20;
  if (isRacing && Array.isArray(event?.runners) && pick_outcome != null) {
    const asNum = Number(pick_outcome);
    if (Number.isFinite(asNum) && asNum >= 1 && event!.runners!.length > 0 && asNum <= event!.runners!.length) {
      out['runner_number'] = asNum;
    } else {
      const idx = event!.runners!.findIndex(
        (r) => r && r.runner_id != null && String(r.runner_id) === String(pick_outcome)
      );
      if (idx >= 0) {
        out['runner_number'] = idx + 1;
      }
    }
  }
  const isFootball3d = parseInt(String(event?.sport_id)) === 50;
  const home = event?.home_team?.code || event?.home_team_code || '';
  const away = event?.away_team?.code || event?.away_team_code || '';
  const football3dDescription = home && away ? `${home} vs ${away}` : null;
  out['description'] = isFootball3d
    ? (football3dDescription || event?.description || null)
    : (event?.description || null);

  const matched = outcomes[outcome_id];
  if (matched?.outcome_type_id != null) {
    out['outcome_type_id'] = matched.outcome_type_id;
  }
  if (matched?.market_type_id != null) {
    out['market_type_id'] = matched.market_type_id;
  } else if (market_type_id != null) {
    out['market_type_id'] = market_type_id;
  }

  return out as OutcomeItem;
}

/** Tip item with at least event_id and outcome_id. */
export interface TipItem {
  event_id?: number | string;
  outcome_id?: number | string;
  odd?: number | string;
  stake?: number | string;
  [key: string]: unknown;
}

export type SystemBetPreviewRow = {
  size: number;
  label: string;
  combinationCount: number;
  stakePerLine: number;
  totalStake: number;
  minimumPotentialPayout: number;
  maximumPotentialPayout: number;
  totalPotentialPayout: number;
};

export type SystemBetPreview = {
  valid: boolean;
  invalidReason: 'ZERO_EFFECTIVE_SYSTEM' | null;
  totalStake: number;
  minimumPotentialPayout: number;
  maximumPotentialPayout: number;
  totalPotentialPayout: number;
  totalCombinations: number;
  rows: SystemBetPreviewRow[];
};

export type DutchingPreview = {
  valid: boolean;
  totalStake: number;
  minimumPotentialPayout: number;
  maximumPotentialPayout: number;
};

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Returns true if the tips array contains more than one distinct event_id.
 */
export function hasMultipleEvents(tips: TipItem[]): boolean {
  return [...new Set(tips.map(({ event_id }) => event_id))].length > 1;
}

export function countDistinctEvents(tips: TipItem[]): number {
  return new Set(
    tips
      .map(({ event_id }) => event_id)
      .filter((eventId) => eventId != null)
      .map((eventId) => String(eventId))
  ).size;
}

export function hasDuplicateEvents(tips: TipItem[]): boolean {
  if (tips.length === 0) return false;
  return countDistinctEvents(tips) !== tips.length;
}

export function sumTipStakes(tips: TipItem[]): number {
  return roundMoney(
    tips.reduce((sum, tip) => {
      const stake = Number(tip.stake);
      return Number.isFinite(stake) && stake > 0 ? sum + stake : sum;
    }, 0)
  );
}

export function hasPositiveTipStakes(tips: TipItem[]): boolean {
  return tips.some((tip) => {
    const stake = Number(tip.stake);
    return Number.isFinite(stake) && stake > 0;
  });
}

/**
 * Returns true if adding new_tip to tips would result in multiple events (more than one event_id).
 */
export function isMultiple(new_tip: TipItem | null | undefined, tips: TipItem[]): boolean {
  const events: (number | string | undefined)[] = [];
  if (!new_tip) {
    for (let i = 0; i < tips.length; i++) {
      if (!events.includes(tips[i].event_id)) {
        events.push(tips[i].event_id);
        if (events.length > 1) return true;
      }
    }
    return false;
  }
  const tickets = Object.create(tips) as TipItem[];
  tickets.push(new_tip);
  for (let i = 0; i < tickets.length; i++) {
    if (!events.includes(tickets[i].event_id)) {
      events.push(tickets[i].event_id);
      if (events.length > 1) return true;
    }
  }
  return false;
}

/**
 * Validates that tips (or tips + new_tip) do not contain duplicate event_id (one outcome per event).
 * Returns false if duplicate event found, true otherwise.
 */
export function checkMultiple(
  new_tip: TipItem | null | undefined,
  tips: TipItem[]
): boolean {
  const events: (number | string | undefined)[] = [];
  if (!new_tip) {
    if (isMultiple(new_tip, tips)) {
      for (let i = 0; i < tips.length; i++) {
        if (!events.includes(tips[i].event_id)) {
          events.push(tips[i].event_id);
        } else {
          return false;
        }
      }
    }
    return true;
  }
  const tickets = Object.create(tips) as TipItem[];
  tickets.push(new_tip);
  for (let i = 0; i < tickets.length; i++) {
    if (!events.includes(tickets[i].event_id)) {
      events.push(tickets[i].event_id);
    } else {
      return false;
    }
  }
  return true;
}

export function getSystemRowSizes(selectionCount: number): number[] {
  if (!Number.isInteger(selectionCount) || selectionCount < 3) {
    return [];
  }

  return Array.from({ length: selectionCount - 2 }, (_, index) => index + 2);
}

export function isSystemRelevant(tips: TipItem[]): boolean {
  return getSystemRowSizes(tips.length).length > 0;
}

export function hasActiveSystemRows(rows: SystemBetRow[]): boolean {
  return rows.some((row) => {
    const stakePerLine = Number(row.stake_per_line);
    return Number.isFinite(stakePerLine) && stakePerLine > 0;
  });
}

export function syncSystemRows(
  tips: TipItem[],
  existingRows: SystemBetRow[]
): SystemBetRow[] {
  const existingRowsBySize = new Map(
    existingRows.map((row) => [Number(row.size), Number(row.stake_per_line) || 0])
  );

  return getSystemRowSizes(tips.length).map((size) => ({
    size,
    stake_per_line: existingRowsBySize.get(size) ?? 0,
  }));
}

function combinationCount(n: number, k: number): number {
  if (!Number.isInteger(n) || !Number.isInteger(k) || k < 0 || k > n) {
    return 0;
  }

  const m = Math.min(k, n - k);
  if (m === 0) return 1;

  let count = 1;
  for (let i = 1; i <= m; i += 1) {
    count = (count * (n - m + i)) / i;
  }
  return Math.round(count);
}

function generateSelectionCombinations<T>(items: T[], size: number): T[][] {
  const combinations: T[][] = [];

  function visit(startIndex: number, acc: T[]) {
    if (acc.length === size) {
      combinations.push([...acc]);
      return;
    }

    for (let index = startIndex; index <= items.length - (size - acc.length); index += 1) {
      acc.push(items[index] as T);
      visit(index + 1, acc);
      acc.pop();
    }
  }

  visit(0, []);
  return combinations;
}

function containsDuplicateEventSelections(selections: TipItem[]): boolean {
  const eventIds = new Set<string>();
  for (const selection of selections) {
    if (selection.event_id == null) {
      continue;
    }
    const eventKey = String(selection.event_id);
    if (eventIds.has(eventKey)) {
      return true;
    }
    eventIds.add(eventKey);
  }
  return false;
}

export function calculateSystemPreview(
  tips: TipItem[],
  rows: SystemBetRow[]
): SystemBetPreview {
  const normalizedRows = rows
    .map((row) => ({
      size: Number(row.size),
      stakePerLine: Number(row.stake_per_line),
    }))
    .filter(
      (row) =>
        Number.isInteger(row.size) &&
        row.size >= 2 &&
        row.size < tips.length &&
        Number.isFinite(row.stakePerLine) &&
        row.stakePerLine > 0
    )
    .sort((left, right) => left.size - right.size);

  const normalizedTips = tips
    .map((tip) => ({
      event_id: tip.event_id,
      outcome_id: tip.outcome_id,
      odd: Number(tip.odd),
    }))
    .filter((tip) => tip.event_id != null && tip.outcome_id != null && Number.isFinite(tip.odd) && tip.odd > 0);

  const previewRows = normalizedRows.map<SystemBetPreviewRow>((row) => {
    const combinations = generateSelectionCombinations(normalizedTips, row.size).filter(
      (combination) => !containsDuplicateEventSelections(combination)
    );
    const potentialPayouts = combinations.map((combination) => {
      const combinedOdd = combination.reduce((product, tip) => product * tip.odd, 1);
      return roundMoney(combinedOdd * row.stakePerLine);
    });

    return {
      size: row.size,
      label: `${row.size}/${tips.length}`,
      combinationCount: combinations.length,
      stakePerLine: row.stakePerLine,
      totalStake: roundMoney(row.stakePerLine * combinations.length),
      minimumPotentialPayout: potentialPayouts.length > 0 ? Math.min(...potentialPayouts) : 0,
      maximumPotentialPayout: potentialPayouts.length > 0 ? Math.max(...potentialPayouts) : 0,
      totalPotentialPayout: roundMoney(potentialPayouts.reduce((sum, payout) => sum + payout, 0)),
    };
  });

  const hasInvalidActiveRows = previewRows.some((row) => row.combinationCount === 0);
  const effectiveRows = previewRows.filter((row) => row.combinationCount > 0);

  return {
    valid: !hasInvalidActiveRows,
    invalidReason: hasInvalidActiveRows ? 'ZERO_EFFECTIVE_SYSTEM' : null,
    totalStake: roundMoney(effectiveRows.reduce((sum, row) => sum + row.totalStake, 0)),
    minimumPotentialPayout: roundMoney(effectiveRows.reduce((sum, row) => sum + row.minimumPotentialPayout, 0)),
    maximumPotentialPayout: roundMoney(effectiveRows.reduce((sum, row) => sum + row.maximumPotentialPayout, 0)),
    totalPotentialPayout: roundMoney(effectiveRows.reduce((sum, row) => sum + row.totalPotentialPayout, 0)),
    totalCombinations: effectiveRows.reduce((sum, row) => sum + row.combinationCount, 0),
    rows: effectiveRows,
  };
}

export function calculateDutchingPreview(tips: TipItem[]): DutchingPreview {
  const normalizedTips = tips
    .map((tip) => ({
      stake: Number(tip.stake),
      odd: Number(tip.odd),
    }))
    .filter((tip) => Number.isFinite(tip.stake) && tip.stake > 0 && Number.isFinite(tip.odd) && tip.odd > 0);

  if (normalizedTips.length !== tips.length || normalizedTips.length === 0) {
    return {
      valid: false,
      totalStake: 0,
      minimumPotentialPayout: 0,
      maximumPotentialPayout: 0,
    };
  }

  const payouts = normalizedTips.map((tip) => roundMoney(tip.stake * tip.odd));

  return {
    valid: true,
    totalStake: roundMoney(normalizedTips.reduce((sum, tip) => sum + tip.stake, 0)),
    minimumPotentialPayout: roundMoney(Math.min(...payouts)),
    maximumPotentialPayout: roundMoney(payouts.reduce((sum, payout) => sum + payout, 0)),
  };
}

export function getTicketSlipResolvedType(
  tips: TipItem[],
  betMode: TicketBetMode,
  systemRows: SystemBetRow[] = [],
  stakeMode: TicketStakeMode = 'total',
): TicketSlipResolvedType {
  if (betMode === 'SYSTEM' || hasActiveSystemRows(systemRows)) return 'SYSTEM';
  if (tips.length <= 1) return 'SINGLE';
  if (stakeMode === 'per_tip') return 'DUTCHING';
  return 'MULTIPLE';
}

/**
 * Returns true if outcome_id is already present in tips (by outcome_id match).
 */
export function checkOutcomeInTicket(
  outcome_id: number | string,
  tips: TipItem[]
): boolean {
  return !!tips.find((tip) => parseInt(String(outcome_id)) === parseInt(String(tip.outcome_id)));
}

/**
 * Multiplies all valid odds in tips and returns the total. Returns 0 if no valid odds.
 */
export function calculateTotalOdds(tips: TipItem[] | null | undefined): number {
  if (!tips || tips.length === 0) {
    return 0;
  }

  const validOdds = tips
    .map(({ odd }) => odd)
    .filter((odd) => odd != null && !isNaN(Number(odd)))
    .map((odd) => parseFloat(String(odd)));

  if (validOdds.length === 0) {
    return 0;
  }

  if (validOdds.length === 1) {
    return validOdds[0]!;
  }

  const total_odds = validOdds.reduce((a, b) => a * b);
  return isNaN(total_odds) || !isFinite(total_odds) ? 0 : total_odds;
}
